import _ from 'lodash';
import { useState, useContext, useMemo } from "react";
import { useMsal, } from '@azure/msal-react';
import Comparer from "../../app/comparer";
import { Panel, PanelBody, PanelHeader } from "../panel/panel";
import { GoogleMap } from '../map/map';
import { MetaDataTable } from '../properties/metadata-table';
import LoadingAnimation from "../loading/loading";
import { FloatingEmailInput } from "../reusable-elements/floating-email-input";
import { FloatingTextInput } from "../reusable-elements/floating-text-input";
import { validEmail, validUKPostCode, notNullOrEmpty, validUKPhoneNumber, validateField } from "../../utils/validators";
import { CompanyContext } from "../../pages/company/company-detail";
import { ImageUploadModal } from "../files/image-upload-modal";
import { FileApi } from "../../api/file/file-api";
import { googleMapsConfig } from '../../app';

import './company-styles.css'

export const CompanyDetailsTab = ({ company }) => {
    const { instance, inProgress, accounts } = useMsal();

    const fileApi = useMemo(() => new FileApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const comparer = new Comparer();

    const { saveCompany } = useContext(CompanyContext);

    const defaultAddress = {
        doorNumberOrName: '',
        roadName: '',
        town: '',
        postalCode: ''
    };

    const getAddress = obj => obj && obj.address ? obj.address : defaultAddress;

    const [state, setState] = useState(structuredClone({ ...company, address: getAddress(company) }));
    const [originalState, setOriginalState] = useState(structuredClone({ ...company, address: getAddress(company) }));
    const [validationErrors, setValidationErrors] = useState(null);
    const [showLogoUploadModal, setShowLogoUploadModal] = useState(false);
    const [logoUploadProgress, setLogoUploadProgress] = useState(0);
    const [logoUploadInProgress, setLogoUploadInProgress] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [rerender, setRerender] = useState(false);

    const mapConfig = ({ lat, lng }) => {
        return {
            className: "h-100 w-100",
            style: { height: '100vh', width: '100%' },
            apiKey: googleMapsConfig.apiKey,
            center: {
                lat: lat,
                lng: lng
            },
            zoom: 17
        }
    };

    const validateState = stateToValidate => {
        validateField(() => notNullOrEmpty(stateToValidate.name), () => stateToValidate.name, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.email) && validEmail(stateToValidate.email), () => stateToValidate.email, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.phoneNumber) && validUKPhoneNumber(stateToValidate.phoneNumber), () => stateToValidate.phoneNumber, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.address.doorNumberOrName), () => stateToValidate.address.doorNumberOrName, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.address.roadName), () => stateToValidate.address.roadName, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.address.town), () => stateToValidate.address.town, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.address.postalCode) && validUKPostCode(stateToValidate.address.postalCode), () => stateToValidate.address.postalCode, setValidationErrors);
    };

    const handleStateUpdates = updateMethod => {
        setState(current => {
            const newState = updateMethod(current);
            validateState(newState);
            return newState;
        });
        setRerender(!rerender);
    };

    const hasChanges = () => {
        const equal = comparer.equals(state, originalState);
        return !equal;
    };

    const canSave = () => {
        let result = hasChanges();
        result &&= validationErrors ? Object.keys(validationErrors).length === 0 : result;
        return result;
    };

    const hasLogo = !!state && !!Object.hasOwn(state, 'logoPathIncludingSasToken');

    const companyLogoPath = landlord => {
        return landlord.logoPathIncludingSasToken;
    }

    const imageStyle = company => {
        const unknown = { backgroundImage: 'url(/assets/img/pos/unknown-house.png)' };
        const known = { backgroundImage: `url(${companyLogoPath(company)})` };
        return companyLogoPath(company) ? known : unknown;
    }

    const handleCancel = () => {
        setState(structuredClone(originalState));
        setOriginalState(structuredClone(originalState));
        setValidationErrors(null);
    };

    const uploadFiles = async (path, collection) => {
        try {
            if (!logoUploadInProgress) {
                setLogoUploadInProgress(true);
                const totalSize = _.sumBy(collection, f => f.size);
                const files = await fileApi.uploadFiles(state.name, path, collection, data => {
                    const totalRemaining = _.sumBy(data, d => d.remainingBytes);
                    const progress = 1 - (totalRemaining / totalSize);
                    console.log(`Progress: ${progress * 100} %`);
                    setLogoUploadProgress(progress);
                });
                setLogoUploadInProgress(false);
                setLogoUploadProgress(0);
                return files;
            }
        } catch (error) {
            console.log("Error occurred persisting files.", error);
        }
        finally {
            setShowLogoUploadModal(false)
        }
    };

    const handleSave = async updatedCompany => {
        if (!saveInProgress) {
            setSaveInProgress(true);
            const result = await saveCompany(updatedCompany ?? state);
            setState(result);
            setOriginalState(result);
            setSaveInProgress(false);
        }
    };

    const updateLogo = async images => {
        const result = await uploadFiles(`${state.id}/logo`, images);
        const logoPath = result[0].path;
        const updatedCompany = { ...state, logoPath: logoPath };
        await handleSave(updatedCompany);
        setRerender(!rerender);
    };

    return !!state ? (

        <div className="row mb-3">

            <ImageUploadModal
                show={showLogoUploadModal}
                maxFileCount={1}
                handleCancel={() => setShowLogoUploadModal(false)}
                imageUploadInProgress={logoUploadInProgress}
                imageUploadProgress={logoUploadProgress}
                handleSave={updateLogo}
            />

            <div className="col-xl-4" key={"Details"}>
                <Panel
                    showReload={false}
                    showDelete={false}
                >
                    <PanelHeader>Details</PanelHeader>
                    <PanelBody>
                        <div className="row mb-15px">
                            <label className="form-label col-form-label col-md-3">Logo</label>
                            <div className={'col-md-6'}>
                                <div className="h-250px">
                                    {hasLogo &&
                                        <div onClick={() => setShowLogoUploadModal(true)} className="container img" style={imageStyle(state)}>
                                            <div className='overlay' >
                                                <button className="transparent-btn">Change</button>
                                            </div>
                                        </div>}
                                    {!hasLogo && <div onClick={() => setShowLogoUploadModal(true)}>Please upload a logo or image</div>}
                                </div>
                            </div>
                        </div>

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.name ? "Enter" : ""} Name`}
                            text={state.name}
                            updateCallback={newValue => { handleStateUpdates(v => { v.name = newValue; return v; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.name); }}
                        />

                        <FloatingEmailInput
                            className={"mt-1"}
                            label={`${!state.email ? "Enter" : ""} Email Address`}
                            email={state.email}
                            updateCallback={newValue => { handleStateUpdates(v => { v.email = newValue; return v; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.email) && validEmail(state.email); }}
                        />

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.phoneNumber ? "Enter" : ""} Phone Number`}
                            text={state.phoneNumber}
                            updateCallback={newValue => { handleStateUpdates(v => { v.phoneNumber = newValue; return v; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.phoneNumber) && validUKPhoneNumber(state.phoneNumber); }}
                        />

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.address.doorNumberOrName ? "Enter" : ""} Building Number/Name`}
                            text={state.address.doorNumberOrName}
                            updateCallback={newValue => { handleStateUpdates(v => { v.address.doorNumberOrName = newValue; return v; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.address.doorNumberOrName); }}
                        />

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.address.roadName ? "Enter" : ""} Street Name`}
                            text={state.address.roadName}
                            updateCallback={newValue => { handleStateUpdates(v => { v.address.roadName = newValue; return v; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.address.roadName); }}
                        />

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.address.town ? "Enter" : ""} Town`}
                            text={state.address.town}
                            updateCallback={newValue => { handleStateUpdates(v => { v.address.town = newValue; return v; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.address.town); }}
                        />

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.address.postalCode ? "Enter" : ""} Post Code`}
                            text={state.address.postalCode}
                            updateCallback={newValue => { handleStateUpdates(v => { v.address.postalCode = newValue; return v; }) }}
                            required={true}
                            validatorFn={() => { return validUKPostCode(state.address.postalCode); }}
                        />

                        <div className="mt-3">
                            {canSave() &&
                                <button title='Save' className="btn btn-primary me-10px" onClick={() => handleSave(state)}>
                                    <i title='Save' className="fa fa-save"></i>&nbsp;&nbsp;Save
                                </button>
                            }
                            {hasChanges() &&
                                <button title='Cancel' className="btn btn-warning" onClick={handleCancel}>
                                    <i title='Cancel' className="fa fa-cancel"></i>&nbsp;&nbsp;Cancel
                                </button>
                            }
                        </div>

                        <MetaDataTable data={state} />
                    </PanelBody>
                </Panel>
            </div>
            <div className="col-xl-8" key={"Map"}>
                <Panel
                    showReload={false}
                    showDelete={false}
                >
                    <PanelHeader>Map</PanelHeader>
                    <PanelBody>
                        <div>
                            {state.coordinates ? <GoogleMap {...mapConfig({ lat: state.coordinates.latitude, lng: state.coordinates.longitude })}
                                markers={[{
                                    icon: "fa fa-location-dot",
                                    name: "HQ",
                                    ...mapConfig({ lat: state.coordinates.latitude, lng: state.coordinates.longitude })
                                }
                                ]}>
                            </GoogleMap> : <>Not Yet Geocoded. Check the address.</>}
                        </div>
                    </PanelBody>
                </Panel>
            </div>
        </div>
    )
        :
        <LoadingAnimation />
}